import {
  Avatar,
  Card,
  Divider,
  Empty,
  Radio,
  RadioChangeEvent,
  Tabs,
  Typography,
} from "antd";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { ReactComponent as ConsentCard } from "../../../../Assets/consentCard.svg";
import empty from "../../../../Assets/emptyStateConsent.png";
import { ReactComponent as Timer } from "../../../../Assets/timer.svg";
import { translation } from "../../../../i18n/translationConstant";
import { ConsentDetail } from "../../../../Models/Response/Consent/GetAllConsent";
import {
  getConsentCounts,
  getPaginatedConsentByStatus,
} from "../../../../Services/ConsentServices/ConsentService";
import { defaultPageSize } from "../../../../Utils/Constants/apiConstants";
import {
  ConsentApprovalStatusType,
  ConsentStatusType,
} from "../../../../Utils/Constants/consentTypes";
import useINF from "../../../Common/Components/InfiniteScroll";
import TimeFromNow from "../../../Common/Components/TimeFromNow";
import "./index.css";

const { TabPane } = Tabs;

interface Consent {
  [key: number]: {
    numberOfConsent: number;
    consentDetails: ConsentDetail[];
    title: string;
  };
}

const ConsentComponent: React.FC = () => {
  const { push } = useHistory();
  const { t } = useTranslation();

  const [consents, setConsents] = useState<Consent>({
    1: {
      numberOfConsent: 0,
      consentDetails: new Array<ConsentDetail>(),
      title: t("Pending Consents"),
    },
    2: {
      numberOfConsent: 0,
      consentDetails: new Array<ConsentDetail>(),
      title: t("Active Consents"),
    },
    3: {
      numberOfConsent: 0,
      consentDetails: new Array<ConsentDetail>(),
      title: t("Other Consents"),
    },
    4: {
      numberOfConsent: 0,
      consentDetails: new Array<ConsentDetail>(),
      title: t("Paused"),
    },
    5: {
      numberOfConsent: 0,
      consentDetails: new Array<ConsentDetail>(),
      title: t("Revoked"),
    },
    6: {
      numberOfConsent: 0,
      consentDetails: new Array<ConsentDetail>(),
      title: t("Rejected"),
    },
    7: {
      numberOfConsent: 0,
      consentDetails: new Array<ConsentDetail>(),
      title: t("Expired"),
    },
  });

  const [page, setPage] = useState(1);
  const pageSize = defaultPageSize;
  const [activeKey, setActiveKey] = useState(1);
  const [status, setStatus] = useState([ConsentStatusType.CREATED]);
  const [hasMore, setHasMore] = useState(true);

  const observer = useRef<any>();

  const { newObserver, ref } = useINF(observer, hasMore, setPage);

  observer.current = newObserver.current;

  const getConsentsByStatus = useCallback(() => {
    getPaginatedConsentByStatus(page - 1, pageSize, status.toString())
      .then((res) => res.data)
      .then((res) => {
        setHasMore(!res.last);
        setConsents((old) => ({
          ...old,
          [activeKey]: {
            ...old[activeKey],
            consentDetails: old[activeKey].consentDetails.concat(res.content),
            numberOfConsent: res.totalElements,
          },
        }));

        getConsentCounts()
          .then((res1) => res1.data)
          .then((res1) => {
            setConsents((old) => ({
              ...old,
              "1": {
                ...old[1],
                numberOfConsent: res1.numberOfPendingConsent,
              },
              "2": {
                ...old[2],
                numberOfConsent: res1.numberOfActiveConsent,
              },
              "3": {
                ...old[3],
                numberOfConsent:
                  res1.numberOfOtherConsent + res1.numberOfPausedConsent,
              },
            }));
          });
      });
  }, [pageSize, page, status, activeKey]);

  useEffect(() => {
    getConsentsByStatus();
  }, [getConsentsByStatus]);

  const returnNumberOfConsents = (key: number) => {
    return (
      <>
        <Typography.Text className="consentTabTitle">
          {consents[key].title}
        </Typography.Text>{" "}
      </>
    );
  };

  const onTabChange = (key: string) => {
    const k = parseInt(key, 10);
    setPage(1);
    setHasMore(true);
    setConsents((old) => ({
      ...old,
      [activeKey]: {
        ...old[activeKey],
        consentDetails: [],
      },
    }));

    switch (k) {
      case 1:
        setStatus([ConsentStatusType.CREATED]);
        setActiveKey(k);
        break;
      case 2:
        setStatus([ConsentStatusType.ACTIVE]);
        setActiveKey(k);
        break;
      case 3:
        setActiveKey(k);
        setStatus([ConsentStatusType.PAUSED]);
        break;
      case 4:
        setStatus([ConsentStatusType.PAUSED]);
        setActiveKey(3);
        break;
      case 5:
        setStatus([ConsentStatusType.REVOKED]);
        setActiveKey(3);
        break;
      case 6:
        setActiveKey(3);
        setStatus([ConsentStatusType.REJECTED]);
        break;
      case 7:
        setActiveKey(3);
        setStatus([ConsentStatusType.EXPIRED]);
        break;
      default:
        break;
    }
  };

  const consentCardClick = (consent: ConsentDetail, index: number) => {
    push({
      pathname: `/consent/${consent.consentStatusType === ConsentStatusType.CREATED
          ? ConsentApprovalStatusType.PENDING
          : consent.consentStatusType
        }/${consent.consentid ? consent.consentid : consent.consentHandle}`,
      state: {
        consentCard: consent,
      },
    });
  };

  const ConsentRender = () => {
    return (
      <>
        {activeKey === 3 ? (
          <Radio.Group
            onChange={radioChange}
            defaultValue={4}
            buttonStyle="solid"
          >
            <Radio.Button className="radioOtherConsent" value={4}>
              {t("Paused")}
            </Radio.Button>
            <Radio.Button className="radioOtherConsent" value={5}>
              {t("Revoked")}
            </Radio.Button>
            <Radio.Button className="radioOtherConsent" value={6}>
              {t("Rejected")}
            </Radio.Button>
            <Radio.Button className="radioOtherConsent" value={7}>
              {t("Expired")}
            </Radio.Button>
          </Radio.Group>
        ) : (
          <></>
        )}
        {consents[activeKey].consentDetails &&
          consents[activeKey].consentDetails.length ? (
          <>
            {consents[activeKey].consentDetails.map(
              (consent: ConsentDetail, index: number) => (
                <Card
                  key={index}
                  hoverable={false}
                  bordered={false}
                  // tslint:disable-next-line: jsx-no-lambda
                  onClick={() => consentCardClick(consent, index)}
                  className={`consentCard`}
                >
                  <Typography.Text className="consentHomeCardFIU">
                    {index + 1}.
                  </Typography.Text>
                  <Avatar className="consentAvatar" src={consent.logoUrl} />

                  <Typography.Text className="consentHomeCardFIU marginLeftFIU width70">
                    {consent.fiuName}{" "}
                    <Typography.Text className="consentHomeLinkAccountTypo">
                     {t(translation.lblWantsToAccessAccount)}
                    </Typography.Text>
                  </Typography.Text>
                  {/* <Divider className="verticalDivider" type="vertical" /> */}
                  {/* <Typography.Text className="fontWeight500 consentHomeLinkAccountTypo width30">
                    {t(translation.lblHandle)} :{" "}
                    <Typography.Text className="fontWeight600 consentHomeLinkAccountTypo">
                      {" "}
                      {consent.consentHandle}
                    </Typography.Text>
                  </Typography.Text>
                  <Divider className="verticalDivider" type="vertical" /> */}
                  <Timer className="timer" />

                  <TimeFromNow
                    className="consentHomeTime"
                    date={consent.updatedOn}
                  />
                </Card>
              )
            )}
            <div className="infiniteDiv" ref={ref} />
          </>
        ) : (
          <Empty
            image={empty}
            imageStyle={{
              width: 140,
              height: 120,
              display: "flex",
              marginLeft: "auto",
              marginRight: "auto",
              marginTop: "50px",
            }}
            className="empty"
            description={
              <>
                <Typography.Text className={`consentErrorMsg`}>
                  Oops!{" "}
                </Typography.Text>
                <Typography.Text className="consentErrorSubText">
                  {t("consentsErrorMeassage", {
                    type: t(status.toString().toLowerCase()),
                  })}
                </Typography.Text>
              </>
            }
          />
        )}
      </>
    );
  };

  const radioChange = (e: RadioChangeEvent) => {
    onTabChange(e.target.value);
  };

  return (
    <div className="consentLayout">
      <div className="consentHeader">
        <Card title={t("Pending Consents")}>{consents[1].numberOfConsent}</Card>
        <Card title={t("Active Consents")}>{consents[2].numberOfConsent}</Card>
        <Card title={t("Other Consents")}>{consents[3].numberOfConsent}</Card>
        <Card>
          <Typography.Text className="msgFinancialGoal">
            {t(translation.msgAchieveFinanceGoals)}
          </Typography.Text>{" "}
          <div className="consentHomeCardIconDiv">
            <ConsentCard className="consentCardIcon" />
          </div>
        </Card>
      </div>
      <Card bordered={false} className="consentDetailsHome">
        <Tabs
          onChange={onTabChange}
          animated={true}
          defaultActiveKey="1"
          className="consentTabs"
        >
          {[1, 2, 3].map((key) => (
            <TabPane
              className="tab"
              tab={returnNumberOfConsents(key)}
              key={key}
              id={key.toString()}
            >
              {activeKey === key ? ConsentRender() : <></>}
            </TabPane>
          ))}
        </Tabs>
      </Card>
    </div>
  );
};

export default ConsentComponent;
